import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient, useLazyQuery, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Groups,
  Lock,
  NotificationsActive,
  PictureAsPdf,
  TableView,
  TrendingDown
} from '@mui/icons-material';
import StatCard from 'components/gamification/StatCard';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import DepartmentDetailDrawer from 'components/admin-organizations/DepartmentDetailDrawer';
import { exportWellbeingToExcel } from 'components/admin-organizations/wellbeingExport';
import { bgOf, toneOf } from 'components/admin-organizations/wellbeingTones';
import {
  ORGANIZATION_WELLBEING,
  WELLBEING_HEATMAP,
  WELLBEING_PARTICIPATION,
  WELLBEING_ALERTS,
  WELLBEING_REPORT_URL,
  ORGANIZATION_DEPARTMENTS,
  ORGANIZATION_DEPARTMENT_ADOPTION
} from 'api/graphql/wellbeing';

const DIMENSIONS = ['energy', 'stress', 'rest', 'motivation'];

const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

// Gauge circular SVG propio (sin librerías): anillo de progreso con el índice al centro.
function WellbeingGauge({ score = null, isProtected = false }) {
  const size = 168;
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const value = isProtected || score === null ? 0 : score;

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flex: 'none' }}>
      <Box component="svg" viewBox={`0 0 ${size} ${size}`} sx={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" strokeWidth={stroke} stroke="currentColor" style={{ color: 'var(--otx-inner-highlight)' }} opacity={0.6} />
        <Box
          component="circle"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - value / 100)}
          sx={{ stroke: 'currentColor', color: toneOf(isProtected ? null : score), transition: 'stroke-dashoffset 700ms cubic-bezier(0.32, 0.72, 0, 1)' }}
        />
      </Box>
      <Stack alignItems="center" justifyContent="center" sx={{ position: 'absolute', inset: 0 }}>
        {isProtected ? (
          <Lock sx={{ color: 'text.disabled', fontSize: 32 }} />
        ) : (
          <Typography sx={{ fontSize: '2.6rem', fontWeight: 800, lineHeight: 1, fontVariantNumeric: 'tabular-nums', color: toneOf(score) }}>
            {score ?? '—'}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

WellbeingGauge.propTypes = { score: PropTypes.number, isProtected: PropTypes.bool };
/**
 * Bienestar Corporativo (admin/RRHH) — tablero de la organización: Índice OTRIX en gauge,
 * participación, tendencia, heatmap por departamento (con candados de privacidad), alertas
 * y export a Excel/PDF. La k-anonymity viene resuelta del backend: aquí solo se pinta.
 */
function OrganizationWellbeingTab({ organizationId, organizationName = '' }) {
  const { t } = useTranslation(['organizations']);
  const client = useApolloClient();
  const [weeks, setWeeks] = useState(4);
  const [departmentId, setDepartmentId] = useState('');
  const [detailDepartment, setDetailDepartment] = useState(null);
  const [exporting, setExporting] = useState(false);

  const variables = { organization_id: organizationId, weeks };
  const { data: summaryData, loading } = useQuery(ORGANIZATION_WELLBEING, {
    variables: { ...variables, department_id: departmentId || null },
    fetchPolicy: 'cache-and-network'
  });
  const { data: heatmapData } = useQuery(WELLBEING_HEATMAP, { variables, fetchPolicy: 'cache-and-network' });
  const { data: participationData } = useQuery(WELLBEING_PARTICIPATION, {
    variables: { organization_id: organizationId, weeks: Math.max(weeks, 8) },
    fetchPolicy: 'cache-and-network'
  });
  const { data: alertsData } = useQuery(WELLBEING_ALERTS, { variables: { organization_id: organizationId }, fetchPolicy: 'cache-and-network' });
  const { data: departmentsData } = useQuery(ORGANIZATION_DEPARTMENTS, { variables: { id: organizationId }, fetchPolicy: 'cache-and-network' });
  const [fetchReportUrl, { loading: loadingPdf }] = useLazyQuery(WELLBEING_REPORT_URL, { fetchPolicy: 'network-only' });

  const summary = summaryData?.organizationWellbeing || null;
  const heatmap = heatmapData?.wellbeingHeatmap || [];
  const participation = participationData?.wellbeingParticipation || [];
  const alerts = alertsData?.wellbeingAlerts || [];
  const departments = departmentsData?.organization?.departments || [];

  const handlePdf = async () => {
    const { data } = await fetchReportUrl({ variables });
    const url = data?.organizationWellbeingReportUrl;
    if (url) window.open(url, '_blank', 'noopener');
  };

  // El Excel suma una hoja de adopción por departamento: se pide con la consulta SIN `people`
  // (una por fila del heatmap) para que en el archivo no pueda aparecer ninguna persona.
  const handleExcel = async () => {
    setExporting(true);
    try {
      const usage = await Promise.all(
        heatmap.map((row) =>
          client
            .query({
              query: ORGANIZATION_DEPARTMENT_ADOPTION,
              variables: { organization_id: organizationId, department_id: row.department_id, weeks },
              fetchPolicy: 'network-only'
            })
            .then(({ data }) => data?.organizationDepartmentDetail || null)
            .catch(() => null)
        )
      );
      exportWellbeingToExcel({
        organizationName,
        summary,
        heatmap,
        participation,
        departmentUsage: usage.filter(Boolean)
      });
    } finally {
      setExporting(false);
    }
  };

  const openDepartment = (row) =>
    setDetailDepartment({
      id: row.department_id,
      name: row.department_name || t('wellbeingNoDepartment', { defaultValue: 'Sin departamento' }),
      // El heatmap ya trae el agregado con k-anonymity resuelta: se reusa para no repedirlo.
      wellbeing: { score: row.overall, protected: row.protected }
    });

  const dimensionLabel = (dimension) => t(`wellbeingDimensions.${dimension}`, {
    defaultValue: { energy: 'Energía', stress: 'Estrés', rest: 'Descanso', motivation: 'Motivación' }[dimension] || dimension
  });

  if (loading && !summary) {
    return (
      <Grid container spacing={2}>
        {[0, 1, 2].map((i) => (
          <Grid item xs={12} md={4} key={i}>
            <Skeleton variant="rounded" height={180} sx={{ borderRadius: '16px' }} />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Box>
      {/* Filtros + exports */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 2.5 }}>
        <Stack direction="row" spacing={1.5}>
          <TextField select size="small" label={t('wellbeingWindow', { defaultValue: 'Ventana' })} value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} sx={{ minWidth: 140 }}>
            {[4, 8, 12].map((w) => (
              <MenuItem key={w} value={w}>
                {t('wellbeingWeeks', { count: w, defaultValue: '{{count}} semanas' })}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('wellbeingDepartment', { defaultValue: 'Departamento' })}
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 170 }}>
            <MenuItem value="">{t('wellbeingAllDepartments', { defaultValue: 'Toda la organización' })}</MenuItem>
            {departments.map((department) => (
              <MenuItem key={department.id} value={department.id}>
                {department.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button size="small" startIcon={<TableView sx={{ fontSize: 16 }} />} onClick={handleExcel} disabled={exporting} sx={{ color: 'gold.main', textTransform: 'none', fontWeight: 700 }}>
            Excel
          </Button>
          <Button size="small" startIcon={<PictureAsPdf sx={{ fontSize: 16 }} />} onClick={handlePdf} disabled={loadingPdf} sx={{ color: 'gold.main', textTransform: 'none', fontWeight: 700 }}>
            PDF
          </Button>
        </Stack>
      </Stack>

      {summary && (
        <Grid container spacing={2.5}>
          {/* Índice + dimensiones */}
          <Grid item xs={12} md={5}>
            <PremiumCard sx={{ height: '100%' }} innerSx={{ height: '100%' }}>
              <Stack spacing={2} alignItems="center" sx={{ py: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  {t('wellbeingIndex', { defaultValue: 'Índice OTRIX de Bienestar' })}
                </Typography>
                <WellbeingGauge score={summary.score} isProtected={summary.protected} />
                <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                  {summary.protected
                    ? t('wellbeingProtected', { defaultValue: 'Protegido por privacidad: se necesitan al menos 5 personas respondiendo.' })
                    : t('wellbeingRespondents', {
                        respondents: summary.respondents,
                        members: summary.members,
                        percent: summary.participation_percent,
                        defaultValue: '{{respondents}} de {{members}} personas ({{percent}}%)'
                      })}
                </Typography>
                <Stack spacing={1} sx={{ width: '100%' }}>
                  {DIMENSIONS.map((dimension) => {
                    const row = (summary.dimensions || []).find((d) => d.dimension === dimension);
                    const score = row?.protected ? null : row?.score ?? null;
                    return (
                      <Stack key={dimension} direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" fontWeight={700} sx={{ width: 86, flex: 'none' }}>
                          {dimensionLabel(dimension)}
                        </Typography>
                        <Box sx={{ flex: 1, height: 6, borderRadius: 999, backgroundColor: 'surface.overlay', overflow: 'hidden' }}>
                          <Box
                            sx={{
                              height: '100%',
                              width: '100%',
                              borderRadius: 999,
                              backgroundColor: toneOf(score),
                              transform: `scaleX(${(score || 0) / 100})`,
                              transformOrigin: 'left',
                              transition: 'transform 600ms cubic-bezier(0.32, 0.72, 0, 1)'
                            }}
                          />
                        </Box>
                        <Typography variant="caption" fontWeight={800} sx={{ width: 34, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: toneOf(score) }}>
                          {score ?? '—'}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              </Stack>
            </PremiumCard>
          </Grid>

          {/* Tendencia + participación */}
          <Grid item xs={12} md={7}>
            <Stack spacing={2.5} sx={{ height: '100%' }}>
              <PremiumCard>
                <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5 }}>
                  {t('wellbeingTrend', { defaultValue: 'Tendencia del índice' })}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ height: 90 }}>
                  {(summary.trend || []).map((point) => (
                    <Tooltip key={point.week_key} title={point.protected ? t('wellbeingProtectedShort', { defaultValue: 'Protegido' }) : `${point.week_key}: ${point.score ?? '—'}`}>
                      <Stack spacing={0.5} alignItems="center" sx={{ flex: 1, minWidth: 0, height: '100%', justifyContent: 'flex-end' }}>
                        <Box
                          sx={{
                            width: '100%',
                            maxWidth: 30,
                            height: `${point.score ? Math.max(8, point.score) : 4}%`,
                            borderRadius: '6px 6px 2px 2px',
                            backgroundColor: point.score ? toneOf(point.score) : 'surface.overlay',
                            border: '1px solid',
                            borderColor: point.score ? 'transparent' : 'hairline.main',
                            transition: 'height 500ms cubic-bezier(0.32, 0.72, 0, 1)'
                          }}
                        />
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.58rem' }} noWrap>
                          {point.week_key.slice(-3)}
                        </Typography>
                      </Stack>
                    </Tooltip>
                  ))}
                </Stack>
              </PremiumCard>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <StatCard
                    title={t('wellbeingParticipationTitle', { defaultValue: 'Participación (ventana)' })}
                    value={summary.protected ? '—' : `${summary.participation_percent}%`}
                    icon={<Groups sx={{ fontSize: 22 }} />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StatCard
                    title={t('wellbeingAlertsTitle', { defaultValue: 'Alertas activas' })}
                    value={alerts.length}
                    icon={<NotificationsActive sx={{ fontSize: 22 }} />}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Grid>

          {/* Heatmap por departamento */}
          <Grid item xs={12}>
            <PremiumCard>
              <Typography variant="subtitle2" fontWeight={800}>
                {t('wellbeingHeatmap', { defaultValue: 'Mapa por departamento' })}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                {t('wellbeingHeatmapHint', { defaultValue: 'Abre un departamento para ver cuánto usa la app su gente.' })}
              </Typography>
              {heatmap.length === 0 ? (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('wellbeingHeatmapEmpty', { defaultValue: 'Crea departamentos y asigna miembros para ver el mapa de calor.' })}
                </Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Box sx={{ minWidth: 640 }}>
                    {/* Cabecera */}
                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                      <Typography variant="caption" fontWeight={800} sx={{ width: 170, flex: 'none', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.62rem' }}>
                        {t('wellbeingDepartment', { defaultValue: 'Departamento' })}
                      </Typography>
                      {[t('wellbeingOverall', { defaultValue: 'General' }), ...DIMENSIONS.map(dimensionLabel)].map((label) => (
                        <Typography key={label} variant="caption" fontWeight={800} sx={{ flex: 1, textAlign: 'center', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.62rem' }}>
                          {label}
                        </Typography>
                      ))}
                    </Stack>
                    <Stack spacing={0.75}>
                      {heatmap.map((row) => (
                        <Stack
                          key={row.department_id || 'none'}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          role="button"
                          tabIndex={0}
                          aria-label={t('viewDepartmentDetail', {
                            name: row.department_name || t('wellbeingNoDepartment', { defaultValue: 'Sin departamento' }),
                            defaultValue: 'Ver el detalle de uso de {{name}}'
                          })}
                          onClick={() => openDepartment(row)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openDepartment(row);
                            }
                          }}
                          sx={{
                            px: 0.75,
                            py: 0.5,
                            borderRadius: '10px',
                            cursor: 'pointer',
                            transition: `background-color 200ms ${EASE}, transform 200ms ${EASE}`,
                            '&:hover': { backgroundColor: 'surface.overlay' },
                            '&:active': { transform: 'scale(0.98)' },
                            '&:focus-visible': { outline: '2px solid var(--otx-focus-ring)', outlineOffset: 2 }
                          }}>
                          <Box sx={{ width: 170, flex: 'none', minWidth: 0 }}>
                            <Typography variant="caption" fontWeight={700} noWrap>
                              {row.department_name || t('wellbeingNoDepartment', { defaultValue: 'Sin departamento' })}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', fontSize: '0.6rem' }}>
                              {t('departmentMembers', { count: row.members, defaultValue: '{{count}} miembros' })}
                            </Typography>
                          </Box>
                          {[{ score: row.overall, protected: row.protected }, ...(row.cells || [])].map((cell, i) => (
                            <Tooltip
                              // eslint-disable-next-line react/no-array-index-key
                              key={i}
                              title={cell.protected ? t('wellbeingProtectedCell', { defaultValue: 'Protegido por privacidad (grupo menor a 5 personas)' }) : ''}>
                              <Box
                                sx={{
                                  flex: 1,
                                  py: 1,
                                  borderRadius: '8px',
                                  textAlign: 'center',
                                  backgroundColor: cell.protected ? 'surface.overlay' : bgOf(cell.score),
                                  border: '1px solid',
                                  borderColor: 'hairline.main'
                                }}>
                                {cell.protected ? (
                                  <Lock sx={{ fontSize: 13, color: 'text.disabled' }} />
                                ) : (
                                  <Typography variant="caption" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums', color: toneOf(cell.score) }}>
                                    {cell.score ?? '—'}
                                  </Typography>
                                )}
                              </Box>
                            </Tooltip>
                          ))}
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              )}
            </PremiumCard>
          </Grid>

          {/* Alertas */}
          {alerts.length > 0 && (
            <Grid item xs={12}>
              <PremiumCard>
                <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5 }}>
                  {t('wellbeingAlerts', { defaultValue: 'Alertas tempranas' })}
                </Typography>
                <Stack spacing={1}>
                  {alerts.map((alert) => (
                    <Stack
                      key={alert.id}
                      direction="row"
                      spacing={1.25}
                      alignItems="center"
                      sx={{ p: 1.5, borderRadius: '12px', backgroundColor: 'sessionStatus.pending.bg' }}>
                      <TrendingDown sx={{ color: 'sessionStatus.pending.fg', flex: 'none', fontSize: 20 }} />
                      <Typography variant="caption" fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>
                        {t('wellbeingAlertRow', {
                          scope: alert.department_name || t('wellbeingAllDepartments', { defaultValue: 'Toda la organización' }),
                          dimension: alert.dimension === 'overall' ? t('wellbeingOverall', { defaultValue: 'General' }) : dimensionLabel(alert.dimension),
                          from: Math.round(alert.previous_score),
                          to: Math.round(alert.current_score),
                          week: alert.week_key,
                          defaultValue: '{{scope}} · {{dimension}}: bajó de {{from}} a {{to}} en la semana {{week}}'
                        })}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </PremiumCard>
            </Grid>
          )}
        </Grid>
      )}

      <DepartmentDetailDrawer
        open={Boolean(detailDepartment)}
        onClose={() => setDetailDepartment(null)}
        organizationId={organizationId}
        department={detailDepartment}
        weeks={weeks}
        wellbeing={detailDepartment?.wellbeing || null}
      />
    </Box>
  );
}

OrganizationWellbeingTab.propTypes = {
  organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  organizationName: PropTypes.string
};

export default OrganizationWellbeingTab;
